import React, { useState, useEffect } from 'react';
import { Calendar, Gift, Heart, Send, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { CardOccasion } from '../types/cards';
import { formatDisplayDate, getDaysUntil } from '../lib/dateUtils';

interface Contact {
  id: string;
  name: string;
  birthday: string | null;
  anniversary: string | null;
  relationship: string;
  card_preference: string;
  gender: 'male' | 'female' | null;
}

interface UpcomingEvent {
  id: string;
  contactId: string;
  contactName: string;
  date: string;
  sendByDate: string;
  occasion: CardOccasion;
  relationship: string;
  card_preference: string;
  gender: 'male' | 'female' | null;
  hasCardOrder: boolean;
}

interface EventFromDB {
  event_type: string;
  contact_id: string;
  contact_name: string;
  event_date: string;
  send_by_date: string;
  relationship: string;
  card_preference: string;
  gender: 'male' | 'female' | null;
  has_card_order: boolean;
}

interface UpcomingEventsProps {
  onSelectEvent: (event: UpcomingEvent) => void;
}

export function UpcomingEvents({ onSelectEvent }: UpcomingEventsProps) {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  async function fetchUpcomingEvents() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get events with send-by dates
      const { data: upcomingEvents, error } = await supabase
        .rpc('get_events_with_send_by_dates', {
          p_user_id: session.user.id,
          p_days_ahead: 30
        });

      if (error) throw error;

      // Transform the data into our UpcomingEvent format
      const formattedEvents: UpcomingEvent[] = upcomingEvents.map((event: EventFromDB) => ({
        id: `${event.event_type}-${event.contact_id}`,
        contactId: event.contact_id,
        contactName: event.contact_name,
        date: event.event_date,
        sendByDate: event.send_by_date,
        occasion: event.event_type as CardOccasion,
        relationship: event.relationship,
        card_preference: event.card_preference,
        gender: event.gender,
        hasCardOrder: event.has_card_order
      }));

      // Sort events by date
      formattedEvents.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {events.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-gray-500">No upcoming birthdays or anniversaries in the next 30 days.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((event) => {
            const daysUntilEvent = getDaysUntil(event.date);
            const daysUntilSendBy = getDaysUntil(event.sendByDate);
            
            // Define warning states based on event date
            const isEmergency = daysUntilEvent <= 10 && !event.hasCardOrder; // 10 days or less until event
            const isWarning = daysUntilEvent <= 14 && daysUntilEvent > 10 && !event.hasCardOrder; // 11-14 days until event
            const isPlain = daysUntilEvent > 14 || event.hasCardOrder; // More than 14 days until event or already sent

            // Helper function to get card styles based on state
            const getCardStyles = () => {
              if (event.hasCardOrder) return 'border-green-200 bg-green-50';
              if (isEmergency) return 'border-red-500 bg-white';
              if (isWarning) return 'border-yellow-500 bg-white';
              return 'border-gray-200 bg-white hover:border-brand-primary';
            };

            // Helper function to get icon styles based on state
            const getIconStyles = () => {
              if (event.hasCardOrder) return 'bg-green-100';
              if (isEmergency) return 'bg-red-100';
              if (isWarning) return 'bg-yellow-100';
              return 'bg-brand-primary/10';
            };

            // Helper function to get icon component based on state
            const getIcon = () => {
              if (isEmergency) return <AlertTriangle className="w-5 h-5 text-red-500" />;
              if (isWarning) return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
              if (event.occasion === 'birthday') return <Gift className="w-5 h-5 text-brand-primary" />;
              return <Heart className="w-5 h-5 text-brand-primary" />;
            };

            // Helper function to get send by message styles and text
            const getSendByMessage = () => {
              if (event.hasCardOrder) {
                return { text: 'Card already sent', className: 'text-green-600' };
              }
              if (isEmergency) {
                return { text: `Event in ${daysUntilEvent} days! Send immediately!`, className: 'text-red-500 font-medium' };
              }
              if (isWarning) {
                return { text: `Event in ${daysUntilEvent} days - Send soon!`, className: 'text-yellow-600 font-medium' };
              }
              return { text: `Event in ${daysUntilEvent} days`, className: 'text-gray-500' };
            };

            const sendByMessage = getSendByMessage();

            return (
              <div
                key={event.id}
                className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${getCardStyles()}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getIconStyles()}`}>
                    {getIcon()}
                  </div>
                  <div>
                    <div className="font-medium text-brand-secondary">
                      {event.contactName}
                      {event.hasCardOrder && (
                        <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                          Card Sent
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDisplayDate(event.date)} • {event.occasion.charAt(0).toUpperCase() + event.occasion.slice(1)}
                    </div>
                    <div className="text-xs text-gray-400">
                      {event.relationship} • Prefers {event.card_preference} cards
                    </div>
                    <div className={`text-xs mt-1 ${sendByMessage.className}`}>
                      {sendByMessage.text}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => onSelectEvent(event)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm ${
                    isEmergency
                      ? 'bg-red-500 hover:bg-red-600'
                      : isWarning
                      ? 'bg-yellow-500 hover:bg-yellow-600'
                      : 'bg-brand-primary hover:bg-brand-secondary'
                  } text-white rounded-lg transition-colors`}
                  disabled={event.hasCardOrder}
                >
                  <Send className="w-4 h-4" />
                  {event.hasCardOrder ? 'Sent' : 'Send Card'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
} 